/**
 * @fileoverview TemplateNoteRow — single row in the precedent template
 * notes feed. Ported from `ContactNoteRow` with the table swapped to
 * `precedentTemplateNotes` and the mutations swapped accordingly.
 */

import { useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Loader2, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/primitives/avatar";
import { Button } from "@/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  NotesEditor,
  type NotesEditorHandle,
} from "@/ui/composed/editor/notes-editor";
import { cn } from "@/utils/misc";

export interface TemplateNote {
  _id: Id<"precedentTemplateNotes">;
  content: string;
  createdBy: Id<"users">;
  createdAt: number;
  updatedAt?: number;
  authorDisplayName: string;
  authorAvatarUrl?: string;
  lastEditedByName?: string;
}

interface TemplateNoteRowProps {
  note: TemplateNote;
  canEdit: boolean;
}

function initialsFor(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2);
  if (parts.length === 0) return "?";
  return parts.join("").toUpperCase();
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatAbsolute(ts: number): string {
  return new Date(ts).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function TemplateNoteRow({ note, canEdit }: TemplateNoteRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);
  const editorRef = useRef<NotesEditorHandle | null>(null);

  const updateNote = useConvexMutation(
    api.settings.precedents.notes.updateTemplateNote,
  );
  const deleteNote = useConvexMutation(
    api.settings.precedents.notes.deleteTemplateNote,
  );

  const { mutateAsync: doUpdate, isPending: isSaving } = useMutation({
    mutationFn: (content: string) => updateNote({ noteId: note._id, content }),
  });
  const { mutateAsync: doDelete, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteNote({ noteId: note._id }),
  });

  const handleSave = async () => {
    if (draft === note.content) {
      setIsEditing(false);
      return;
    }
    try {
      await doUpdate(draft);
      toast.success("Note updated");
      setIsEditing(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not save the note";
      toast.error(message);
    }
  };

  const handleCancel = () => {
    setDraft(note.content);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    try {
      await doDelete();
      toast.success("Note deleted");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not delete the note";
      toast.error(message);
    }
  };

  const editedAt =
    note.updatedAt !== undefined && note.updatedAt > note.createdAt
      ? note.updatedAt
      : null;
  const initials = initialsFor(note.authorDisplayName);

  return (
    <article className="rounded-lg border border-border bg-background p-3">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Avatar className="h-7 w-7 shrink-0">
            {note.authorAvatarUrl && (
              <AvatarImage
                src={note.authorAvatarUrl}
                alt={note.authorDisplayName}
              />
            )}
            <AvatarFallback className="text-[10px] font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium leading-tight">
              {note.authorDisplayName}
            </span>
            <span
              className="text-[10px] text-muted-foreground"
              title={formatAbsolute(note.createdAt)}
            >
              {formatRelative(note.createdAt)}
              {editedAt !== null && (
                <>
                  {" · "}
                  <span title={`Edited ${formatAbsolute(editedAt)}`}>
                    edited {formatRelative(editedAt)}
                  </span>
                </>
              )}
            </span>
          </div>
        </div>
        {canEdit && !isEditing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                aria-label="Note actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setIsEditing(true);
                }}
              >
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  void handleDelete();
                }}
                disabled={isDeleting}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>

      {isEditing ? (
        <div className="flex flex-col gap-2">
          <NotesEditor
            ref={editorRef}
            value={draft}
            onChange={setDraft}
            placeholder="Edit the note…"
            minHeightClass="min-h-[120px]"
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void handleSave()}
              disabled={isSaving}
            >
              {isSaving ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : null}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "prose prose-sm max-w-none text-foreground dark:prose-invert",
          )}
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- stored note HTML is DOMPurify.sanitize'd here at the render boundary
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.content) }}
        />
      )}
    </article>
  );
}
